import { Extension, type CommandProps } from "@tiptap/core";

// Indent is a `data-indent` attribute (not inline margin) to keep the sanitizer allow-list a clean data-attribute whitelist.
// List indentation is left to StarterKit's ListKeymap; the keymap below returns false inside a list so that runs.

const MAX_INDENT = 8;

export interface IndentOptions {
  types: string[];
  maxIndent: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    indent: {
      increaseIndent: () => ReturnType;
      decreaseIndent: () => ReturnType;
    };
  }
}

export const Indent = Extension.create<IndentOptions>({
  name: "indent",

  addOptions() {
    return {
      types: ["paragraph", "heading"],
      maxIndent: MAX_INDENT,
    };
  },

  addGlobalAttributes() {
    const maxIndent = this.options.maxIndent;
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const raw = element.getAttribute("data-indent");
              const value = raw ? parseInt(raw, 10) : 0;
              return Number.isFinite(value) && value > 0
                ? Math.min(maxIndent, value)
                : 0;
            },
            renderHTML: (attributes) => {
              const indent = (attributes.indent as number) ?? 0;
              return indent > 0 ? { "data-indent": String(indent) } : {};
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const types = this.options.types;
    const maxIndent = this.options.maxIndent;

    const applyDelta =
      (delta: number) =>
      ({ state, tr, dispatch }: Pick<CommandProps, "state" | "tr" | "dispatch">) => {
        const { from, to } = state.selection;
        let changed = false;
        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!types.includes(node.type.name)) return;
          const current = (node.attrs.indent as number) ?? 0;
          const next = Math.min(maxIndent, Math.max(0, current + delta));
          if (next !== current) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      };

    return {
      increaseIndent: () => applyDelta(1),
      decreaseIndent: () => applyDelta(-1),
    };
  },

  addKeyboardShortcuts() {
    const inList = () =>
      this.editor.isActive("listItem") || this.editor.isActive("taskItem");
    return {
      Tab: () => {
        if (inList()) return false;
        // Always swallow Tab (even at max indent, a no-op) so focus never escapes the editor.
        this.editor.commands.increaseIndent();
        return true;
      },
      "Shift-Tab": () => {
        if (inList()) return false;
        this.editor.commands.decreaseIndent();
        return true;
      },
    };
  },
});
